import { memo } from "react"
import { FiSearch } from "react-icons/fi"

import { HouseholdActionsMenu } from "@/components/layout/Common/HouseholdActionsMenu"
import type { HouseholdResponse } from "@/types/household"
import { EmptyState, Flex, Table, VStack } from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface HouseholdListProps {
  households: HouseholdResponse[]
}

const HouseholdList = memo(({ households }: HouseholdListProps) => {
  if (!households?.length) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>No households yet</EmptyState.Title>
            <EmptyState.Description>
              Create a new household to see it listed here.
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <Table.Root size={{ base: "sm", md: "md" }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="sm">ID</Table.ColumnHeader>
          <Table.ColumnHeader>User Id</Table.ColumnHeader>
          <Table.ColumnHeader>Parent First Name</Table.ColumnHeader>
          <Table.ColumnHeader>Parent Last Name</Table.ColumnHeader>
          <Table.ColumnHeader>Email</Table.ColumnHeader>
          <Table.ColumnHeader>Phone</Table.ColumnHeader>
          <Table.ColumnHeader>Secondary Phone</Table.ColumnHeader>
          <Table.ColumnHeader>Address</Table.ColumnHeader>
          <Table.ColumnHeader>Emergency Contact Name</Table.ColumnHeader>
          <Table.ColumnHeader>Emergency Contact Phone</Table.ColumnHeader>
          <Table.ColumnHeader>
            Emergency Contact Relationship
          </Table.ColumnHeader>
          <Table.ColumnHeader>Doctor First Name</Table.ColumnHeader>
          <Table.ColumnHeader>Doctor Last Name</Table.ColumnHeader>
          <Table.ColumnHeader>Doctor Phone</Table.ColumnHeader>
          <Table.ColumnHeader>Insurance Provider</Table.ColumnHeader>
          <Table.ColumnHeader>Referral Source</Table.ColumnHeader>
          <Table.ColumnHeader>Agreed To Policy</Table.ColumnHeader>
          <Table.ColumnHeader>Agreed To Hold Harmless</Table.ColumnHeader>
          <Table.ColumnHeader>Policy Agreement Date</Table.ColumnHeader>
          <Table.ColumnHeader>Billing Email</Table.ColumnHeader>
          <Table.ColumnHeader>Auto Pay Enabled</Table.ColumnHeader>
          <Table.ColumnHeader>Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Is Active</Table.ColumnHeader>
          <Table.ColumnHeader>Created By</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader>Updated By</Table.ColumnHeader>
          <Table.ColumnHeader>Updated</Table.ColumnHeader>
          <Table.ColumnHeader w="xs">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {households.map((household) => (
          <Table.Row key={household.id}>
            <Table.Cell truncate maxW="sm">
              {household.id}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.user_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.parent_first_name, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.parent_last_name, "str")}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(household.email, "str")}</Table.Cell>
            <Table.Cell>{formatFieldValue(household.phone, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.secondary_phone, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.address, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.emergency_contact_name, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.emergency_contact_phone, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(
                household.emergency_contact_relationship,
                "str",
              )}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.doctor_first_name, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.doctor_last_name, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.doctor_phone, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.insurance_provider, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.referral_source, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.agreed_to_policy, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.agreed_to_hold_harmless, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.policy_agreement_date, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.billing_email, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.auto_pay_enabled, "bool")}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(household.notes, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.is_active, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.created_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.created_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.updated_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(household.updated_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="flex-end">
                <HouseholdActionsMenu household={household} />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
})

HouseholdList.displayName = "HouseholdList"

export default HouseholdList
